import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { DemoBundle, FocusBoundary, Step } from "../../../shared/types";
import {
  clampFocusBoundary,
  moveFocusBoundary,
  resizeFocusBoundary,
  type FocusResizeCorner,
} from "../../../shared/focus";
import { api } from "../../api";
import { setPendingSave, setSaveStatus as setGlobalSaveStatus } from "../dirty";
import { useEditor } from "../DemoEditor";

export interface FocusBoundaryHandle {
  flush: () => Promise<void>;
  isDirty: () => boolean;
}

interface Props {
  step: Step;
  onSaved?: (b: DemoBundle) => void;
  scrollShift?: { dx: number; dy: number };
  onVisualChange?: (b: FocusBoundary) => void;
}

const FocusBoundaryBox = forwardRef<FocusBoundaryHandle, Props>(function FocusBoundaryBox(
  { step, onSaved, scrollShift, onVisualChange },
  ref,
) {
  const { setBundle } = useEditor();
  const [focus, setFocus] = useState<FocusBoundary | null>(step.focus ?? null);
  const [isDragging, setIsDragging] = useState(false);
  const layerRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<
    | { kind: "move"; dx: number; dy: number }
    | { kind: "resize"; corner: FocusResizeCorner }
    | null
  >(null);
  const pending = useRef<{ value: FocusBoundary | null } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(0);

  const shiftX = scrollShift?.dx ?? 0;
  const shiftY = scrollShift?.dy ?? 0;

  // Sync with step.focus when updated externally (e.g. from inspector or presets)
  useEffect(() => {
    if (!dragRef.current) {
      setFocus(step.focus ?? null);
    }
  }, [step.focus]);

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    setPendingSave(false);
    const mine = ++inFlight.current;
    try {
      const b = await api.updateStep(step.id, { focus: p.value });
      if (mine === inFlight.current) {
        setBundle(b);
        onSaved?.(b);
        setGlobalSaveStatus("saved");
      }
    } catch (e) {
      console.error("Failed to save focal boundary", e);
      if (mine === inFlight.current) {
        setGlobalSaveStatus("error", (e as any)?.message ?? "Failed to save focal boundary");
      }
    }
  }, [step.id, setBundle, onSaved]);

  useImperativeHandle(ref, () => ({
    flush,
    isDirty: () => !!pending.current,
  }), [flush]);

  // Flush on unmount so changes are never lost
  useEffect(() => () => {
    if (pending.current) void flush();
  }, [flush]);

  const commit = (next: FocusBoundary, immediate = false) => {
    setFocus(next);
    pending.current = { value: next };
    setPendingSave(true);
    setGlobalSaveStatus("dirty");
    if (timer.current) clearTimeout(timer.current);
    if (immediate) {
      void flush();
    } else {
      timer.current = setTimeout(() => void flush(), 1500);
    }
  };

  const toPct = (clientX: number, clientY: number) => {
    const r = layerRef.current?.getBoundingClientRect();
    if (!r || r.width === 0 || r.height === 0) return { x: 0, y: 0 };
    return {
      x: Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)),
      y: Math.min(100, Math.max(0, ((clientY - r.top) / r.height) * 100)),
    };
  };

  // Visual position on screen is baseline focus shifted by active document scroll displacement
  const visual: FocusBoundary = focus ? {
    ...focus,
    x: Math.round((focus.x + shiftX) * 100) / 100,
    y: Math.round((focus.y + shiftY) * 100) / 100,
  } : { x: 0, y: 0, w: 0, h: 0 };

  const onBoxDown = (e: React.PointerEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).classList.contains("handle")) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const p = toPct(e.clientX, e.clientY);
    dragRef.current = {
      kind: "move",
      dx: p.x - visual.x,
      dy: p.y - visual.y,
    };
    setIsDragging(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };

  const onHandleDown = (e: React.PointerEvent<HTMLSpanElement>, corner: FocusResizeCorner) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      kind: "resize",
      corner,
    };
    setIsDragging(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };

  const onPointerMove = (e: React.PointerEvent<HTMLElement> | PointerEvent) => {
    if (!dragRef.current || !focus) return;
    e.preventDefault();
    const p = toPct(e.clientX, e.clientY);

    if (dragRef.current.kind === "move") {
      const targetVisualX = p.x - dragRef.current.dx;
      const targetVisualY = p.y - dragRef.current.dy;
      const nextVisual = moveFocusBoundary(visual, targetVisualX, targetVisualY);
      const nextBaseline = clampFocusBoundary({
        ...focus,
        x: nextVisual.x - shiftX,
        y: nextVisual.y - shiftY,
        w: nextVisual.w,
        h: nextVisual.h,
      });
      commit(nextBaseline, false);
      onVisualChange?.(nextVisual);
    } else if (dragRef.current.kind === "resize") {
      const nextVisual = resizeFocusBoundary(visual, dragRef.current.corner, p.x, p.y);
      const nextBaseline = clampFocusBoundary({
        ...focus,
        x: nextVisual.x - shiftX,
        y: nextVisual.y - shiftY,
        w: nextVisual.w,
        h: nextVisual.h,
      });
      commit(nextBaseline, false);
      onVisualChange?.(nextVisual);
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLElement> | PointerEvent) => {
    if (!dragRef.current) return;
    e.preventDefault();
    try {
      if ("releasePointerCapture" in e.target! && "pointerId" in e) {
        (e.target as HTMLElement).releasePointerCapture((e as PointerEvent).pointerId);
      }
    } catch {}
    dragRef.current = null;
    setIsDragging(false);
    void flush();
  };

  // Window-level safety listeners during active drag so fast pointer moves never drop
  useEffect(() => {
    if (!isDragging) return;
    const onWinMove = (e: PointerEvent) => onPointerMove(e);
    const onWinUp = (e: PointerEvent) => onPointerUp(e);
    window.addEventListener("pointermove", onWinMove);
    window.addEventListener("pointerup", onWinUp);
    window.addEventListener("pointercancel", onWinUp);
    return () => {
      window.removeEventListener("pointermove", onWinMove);
      window.removeEventListener("pointerup", onWinUp);
      window.removeEventListener("pointercancel", onWinUp);
    };
  }, [isDragging, visual, focus, shiftX, shiftY]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!focus) return;
    const d = e.shiftKey ? 5 : 1;
    let nextX = visual.x;
    let nextY = visual.y;
    if (e.key === "ArrowLeft") nextX -= d;
    else if (e.key === "ArrowRight") nextX += d;
    else if (e.key === "ArrowUp") nextY -= d;
    else if (e.key === "ArrowDown") nextY += d;
    else return;

    e.preventDefault();
    const nextVisual = moveFocusBoundary(visual, nextX, nextY);
    const nextBaseline = clampFocusBoundary({
      ...focus,
      x: nextVisual.x - shiftX,
      y: nextVisual.y - shiftY,
      w: nextVisual.w,
      h: nextVisual.h,
    });
    commit(nextBaseline, true);
    onVisualChange?.(nextVisual);
  };

  if (!focus) return null;

  return (
    <div
      ref={layerRef}
      className="focus-boundary-layer"
      aria-label="Focal Crop Boundary Layer"
    >
      <div
        ref={rectRef}
        className={`focus-boundary-rect ${isDragging ? "dragging" : ""}`}
        tabIndex={0}
        role="region"
        aria-label="Focal Crop Boundary"
        style={{
          left: `${visual.x}%`,
          top: `${visual.y}%`,
          width: `${visual.w}%`,
          height: `${visual.h}%`,
        }}
        onPointerDown={onBoxDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        <div
          className="focus-boundary-bar"
          style={visual.y < 5 ? { top: 4, left: 4 } : undefined}
          title="Drag anywhere on boundary to move focal area"
          onPointerDown={onBoxDown}
        >
          <span>Focus: {Math.round(visual.w)}% × {Math.round(visual.h)}%</span>
        </div>
        {(["se", "sw", "ne", "nw"] as const).map((corner) => (
          <span
            key={corner}
            className={`handle handle-${corner}`}
            title={`Resize ${corner}`}
            onPointerDown={(e) => onHandleDown(e, corner)}
          />
        ))}
      </div>
    </div>
  );
});

export default FocusBoundaryBox;
